import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthShell, TextInput, toast } from "@etherisc/ui-kit";
import { useAuth } from "../hooks/useAuth";
import { PrimaryButton } from "../components/buttons";
import { MyUbuntuLogo } from "../assets/MyUbuntuLogo";
// import { GoogleOAuthButton } from "../components/GoogleOAuthButton";
import type { LoginCredentials } from "../types/Auth";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const [emailForResend, setEmailForResend] = useState<string>('');
  const showError = (message: string, description?: string) => toast.error(message, { description });
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const messageShownRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      // Check if error is about email verification
      if (error.includes('not verified') || error.includes('verification')) {
        // Store email for resend functionality
        const emailInput = document.querySelector<HTMLInputElement>('input[type="email"]');
        if (emailInput?.value) {
          setEmailForResend(emailInput.value);
        }
      }
      showError("Login Failed", error);
      clearError();
    }
  }, [error, showError, clearError]);

  // Handle URL parameters for success/error messages
  useEffect(() => {
    if (messageShownRef.current) return; // Prevent duplicate messages

    const success = searchParams.get('success');
    const errorParam = searchParams.get('error');
    const message = searchParams.get('message');

    if (success === 'email_verified') {
      showSuccess('Email Verified!', message || 'Your email has been verified successfully.');
      messageShownRef.current = true;
    } else if (errorParam === 'verification_failed') {
      showError('Verification Failed', message || 'Email verification failed.');
      messageShownRef.current = true;
    } else if (errorParam === 'invalid_token') {
      showError('Invalid Token', message || 'The verification token is invalid.');
      messageShownRef.current = true;
    }

    // Clear URL parameters after showing message or if password_reset (no toast needed)
    if (success || errorParam) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('success');
      newUrl.searchParams.delete('error');
      newUrl.searchParams.delete('message');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Set flag if we showed a message
      if (success === 'email_verified' || errorParam) {
        messageShownRef.current = true;
      }
    }
  }, [searchParams]); // Removed showSuccess, showError from dependencies

  const onSubmit = async (data: LoginCredentials) => {
    await login(data);
  };

  // const handleGoogleSuccess = () => {
  //   // OAuth success is handled by the GoogleOAuthButton component
  //   // User will be redirected by the useEffect above
  // };

  // const handleGoogleError = () => {
  //   // Error handling is done by the GoogleOAuthButton component
  // };

  return (
    <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Welcome Back</h1>
          <p className="text-gray-600 mt-2">Sign in to your account</p>
        </div>

        {/* Google OAuth Button - Temporarily disabled */}
        {/* <div className="space-y-4">
          <GoogleOAuthButton
            theme="outline"
            size="large"
            text="signin_with"
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">Or continue with email</span>
            </div>
          </div>
        </div> */}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <TextInput
            label="Email"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register("email")}
          />

          <TextInput
            label="Password"
            type="password"
            placeholder="Enter your password"
            error={errors.password?.message}
            {...register("password")}
          />

          <div className="flex items-center justify-between mb-4">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Email verification notice */}
          {emailForResend && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <p className="text-sm text-yellow-800 mb-2 font-medium">
                Email Verification Required
              </p>
              <p className="text-sm text-yellow-700">
                Your email address ({emailForResend}) needs to be verified before you can log in.
                Please check your email for the verification link.
              </p>
            </div>
          )}

          <PrimaryButton type="submit" fullWidth disabled={isLoading}>
            {isLoading ? "Signing in..." : "Sign In"}
          </PrimaryButton>
        </form>

        {/* Demo credentials section - only show in development environments */}
        {(import.meta.env.MODE === 'development' || 
          import.meta.env.VITE_APP_ENV === 'testing' || 
          import.meta.env.VITE_APP_ENV === 'staging') && (
          <div className="border-t pt-6">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 font-medium">Demo credentials (role-based access)</p>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between bg-gray-50 p-2 rounded">
                <span className="font-medium">Admin:</span>
                <span className="text-gray-600">admin@myubuntu.dev / MyUbuntu2025!</span>
              </div>
              <div className="flex justify-between bg-gray-50 p-2 rounded">
                <span className="font-medium">Insurer:</span>
                <span className="text-gray-600">insurer@myubuntu.dev / MyUbuntu2025!</span>
              </div>
              <div className="flex justify-between bg-gray-50 p-2 rounded">
                <span className="font-medium">Farmer:</span>
                <span className="text-gray-600">farmer@myubuntu.dev / MyUbuntu2025!</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
